import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { GakEak } from "../utils/caseFormat";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Briefcase, Calendar, CheckCircle, Clock, ArrowRight, FileSearch } from "lucide-react";

const JudgeCases = () => {
  const { user } = useAuth();
  const [cases, setCases] = useState([]);
  const [hearings, setHearings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [casesRes, hearingsRes] = await Promise.all([
        axios.get(`${API}/judge/my-cases`),
        axios.get(`${API}/judge/upcoming-hearings`)
      ]);
      setCases(casesRes.data);
      setHearings(hearingsRes.data);
    } catch { toast.error("Σφάλμα φόρτωσης δεδομένων"); }
    finally { setLoading(false); }
  };

  const handleStatusChange = async (caseId, status) => {
    try {
      await axios.put(`${API}/judge/update-pretrial-status/${caseId}?status=${status}`);
      toast.success(status === "completed" ? "Ο προδικαστικός ολοκληρώθηκε" : "Η κατάσταση ενημερώθηκε");
      fetchData();
    } catch { toast.error("Σφάλμα ενημέρωσης"); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString("el-GR") : "-";

  const pendingCases = cases.filter(c => c.pretrial_status !== "completed");
  const completedCases = cases.filter(c => c.pretrial_status === "completed");
  const inProgressCases = pendingCases.filter(c => c.pretrial_status === "in_progress");
  const formalReviewCases = pendingCases.filter(c => c.pretrial_status === "formal_review");
  const newCases = pendingCases.filter(c => !c.pretrial_status || c.pretrial_status === "pending");

  const getStatusBadge = (status) => {
    switch (status) {
      case "completed": return <Badge className="bg-green-100 text-green-800">Ολοκληρώθηκε</Badge>;
      case "in_progress": return <Badge className="bg-blue-100 text-blue-800">Σε εξέλιξη</Badge>;
      case "formal_review": return <Badge className="bg-amber-100 text-amber-800">Έλεγχος Τυπικών</Badge>;
      default: return <Badge className="bg-slate-100 text-slate-800">Εκκρεμεί</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="judge-cases-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Οι Υποθέσεις μου</h1>
        <p className="text-slate-500 mt-1">Προδικαστικός έλεγχος ανατεθειμένων υποθέσεων</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card data-testid="stat-total">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold">{cases.length}</p>
            <p className="text-xs text-slate-500">Σύνολο</p>
          </CardContent>
        </Card>
        <Card data-testid="stat-new">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-slate-600">{newCases.length}</p>
            <p className="text-xs text-slate-500">Νέες</p>
          </CardContent>
        </Card>
        <Card data-testid="stat-progress">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-blue-600">{inProgressCases.length}</p>
            <p className="text-xs text-slate-500">Σε εξέλιξη</p>
          </CardContent>
        </Card>
        <Card data-testid="stat-review">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-amber-600">{formalReviewCases.length}</p>
            <p className="text-xs text-slate-500">Τυπικός Έλεγχος</p>
          </CardContent>
        </Card>
        <Card data-testid="stat-done">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-green-600">{completedCases.length}</p>
            <p className="text-xs text-slate-500">Ολοκληρωμένες</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="pending" className="w-full">
        <TabsList className="grid w-full grid-cols-3 bg-slate-100 h-11">
          <TabsTrigger value="pending" className="text-slate-700 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm" data-testid="tab-pending">
            <FileSearch className="w-4 h-4 mr-1" /> Εκκρεμείς ({pendingCases.length})
          </TabsTrigger>
          <TabsTrigger value="completed" className="text-slate-700 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm" data-testid="tab-completed">
            <CheckCircle className="w-4 h-4 mr-1" /> Ολοκληρωμένες ({completedCases.length})
          </TabsTrigger>
          <TabsTrigger value="hearings" className="text-slate-700 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm" data-testid="tab-hearings">
            <Calendar className="w-4 h-4 mr-1" /> Δικάσιμοι ({hearings.length})
          </TabsTrigger>
        </TabsList>

        {/* Pending */}
        <TabsContent value="pending">
          <Card>
            <CardHeader>
              <CardTitle>Εκκρεμείς Υποθέσεις</CardTitle>
              <CardDescription>
                Ροή: Εκκρεμεί → Σε εξέλιξη → Έλεγχος Τυπικών Παραλείψεων → Ολοκλήρωση
              </CardDescription>
            </CardHeader>
            <CardContent>
              {pendingCases.length === 0 ? (
                <p className="text-slate-400 text-sm py-4">Δεν υπάρχουν εκκρεμείς υποθέσεις</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>ΓΑΚ/ΕΑΚ</TableHead>
                      <TableHead>Τύπος</TableHead>
                      <TableHead>Αντικείμενο</TableHead>
                      <TableHead>Δικάσιμος</TableHead>
                      <TableHead>Κατάσταση</TableHead>
                      <TableHead>Ενέργειες</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingCases.map(c => (
                      <TableRow key={c.id}>
                        <TableCell className="font-mono font-semibold">
                          <Link to={`/cases/${c.id}`} className="hover:text-blue-600">
                            <GakEak caseObj={c} />
                          </Link>
                          {c.eak_number && <p className="text-xs text-indigo-600">ΕΑΚ {c.eak_number}</p>}
                        </TableCell>
                        <TableCell><Badge variant="outline">{c.court_type}</Badge></TableCell>
                        <TableCell className="max-w-[120px] truncate text-sm">{c.subject || "-"}</TableCell>
                        <TableCell className="text-sm">{formatDate(c.hearing_date)}</TableCell>
                        <TableCell>{getStatusBadge(c.pretrial_status)}</TableCell>
                        <TableCell>
                          <Select value={c.pretrial_status || "pending"}
                            onValueChange={v => handleStatusChange(c.id, v)}>
                            <SelectTrigger className="w-[160px] h-8 text-xs" data-testid={`status-select-${c.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="in_progress">
                                <span className="flex items-center gap-1"><ArrowRight className="w-3 h-3" /> Σε εξέλιξη</span>
                              </SelectItem>
                              <SelectItem value="formal_review">
                                <span className="flex items-center gap-1"><FileSearch className="w-3 h-3" /> Έλεγχος Τυπικών</span>
                              </SelectItem>
                              <SelectItem value="completed">
                                <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Ολοκλήρωση</span>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Completed */}
        <TabsContent value="completed">
          <Card>
            <CardHeader><CardTitle>Ολοκληρωμένες</CardTitle></CardHeader>
            <CardContent>
              {completedCases.length === 0 ? (
                <p className="text-slate-400 text-sm py-4">Δεν υπάρχουν ολοκληρωμένες</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>ΓΑΚ/ΕΑΚ</TableHead>
                      <TableHead>Τύπος</TableHead>
                      <TableHead>Αντικείμενο</TableHead>
                      <TableHead>Ολοκλήρωση</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {completedCases.map(c => (
                      <TableRow key={c.id}>
                        <TableCell className="font-mono font-semibold">
                          <Link to={`/cases/${c.id}`} className="hover:text-blue-600">
                            <GakEak caseObj={c} />
                          </Link>
                        </TableCell>
                        <TableCell><Badge variant="outline">{c.court_type}</Badge></TableCell>
                        <TableCell className="text-sm">{c.subject || "-"}</TableCell>
                        <TableCell className="text-sm">{formatDate(c.pretrial_completed_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Hearings */}
        <TabsContent value="hearings">
          <Card>
            <CardHeader>
              <CardTitle>Επερχόμενες Δικάσιμοι</CardTitle>
              <CardDescription>Ημερολόγιο προδικαστικού ελέγχου</CardDescription>
            </CardHeader>
            <CardContent>
              {hearings.length === 0 ? (
                <p className="text-slate-400 text-sm py-4">Δεν υπάρχουν δικάσιμοι</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>ΓΑΚ</TableHead>
                      <TableHead>Τύπος</TableHead>
                      <TableHead>Αντικείμενο</TableHead>
                      <TableHead>Δικάσιμος</TableHead>
                      <TableHead>Κατάσταση</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {hearings.map(h => (
                      <TableRow key={h.id}>
                        <TableCell className="font-mono font-semibold"><GakEak caseObj={h} /></TableCell>
                        <TableCell><Badge variant="outline">{h.court_type}</Badge></TableCell>
                        <TableCell className="text-sm">{h.subject || "-"}</TableCell>
                        <TableCell className="font-medium text-indigo-700">{formatDate(h.hearing_date)}</TableCell>
                        <TableCell>{getStatusBadge(h.pretrial_status)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default JudgeCases;
